import { startOfDay, subDays } from "date-fns";
import { useMemo } from "react";

import { type ChartSeriesType } from "@/components/organisms/DateTimeline/DateTimeline.types";

type GeneratedData = { [key: string]: number | Date };
export function useGeneratedData(
  series: ChartSeriesType[],
  numDataPoints?: number,
): GeneratedData[] {
  // Generate default data if none provided, ensuring all dates are at the start of the day.
  const data = useMemo(() => {
    return generateDefaultData(series, numDataPoints);
  }, [numDataPoints]);

  const mappedData = useMemo(
    () =>
      data.map((d) => ({
        ...d,
        date: startOfDay(d.date),
      })) as GeneratedData[],
    [data],
  );

  return mappedData;
}

// Helper function to generate default data
function generateDefaultData(
  series?: ChartSeriesType[],
  numDataPoints = 20,
): GeneratedData[] {
  const today = new Date();
  const data: GeneratedData[] = [];

  for (let i = numDataPoints; i >= 0; i--) {
    const date = subDays(today, i);
    const entry: GeneratedData = {
      date,
    };

    if (series) {
      series.forEach((s) => {
        if (s.type === "line") {
          entry[s.valueKey] = Math.floor(Math.random() * 20) + 10;
        } else if (s.type === "bar") {
          s.valueKeys.forEach((key) => {
            entry[key] = Math.floor(Math.random() * 20) + 5;
          });
        }
      });
    } else {
      // Default case if no series are provided to generate data for
      entry["value"] = Math.floor(Math.random() * 100) + 10;
    }

    data.push(entry);
  }

  return data;
}

export function generateRoundedRectPath(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  isTop: boolean,
  isBottom: boolean,
) {
  if (radius > width / 2) radius = width / 2;
  if (radius > height / 2) radius = height / 2;

  if (!isTop && !isBottom) {
    return `M ${x},${y} h ${width} v ${height} h ${-width} Z`;
  }

  const tl = isTop;
  const tr = isTop;
  const bl = isBottom;
  const br = isBottom;

  let path = `M ${x + radius},${y}`;
  path += ` H ${x + width - radius}`;
  if (tr) path += ` A ${radius},${radius} 0 0 1 ${x + width},${y + radius}`;
  else path += ` H ${x + width} V ${y + radius}`;
  path += ` V ${y + height - radius}`;
  if (br)
    path += ` A ${radius},${radius} 0 0 1 ${x + width - radius},${y + height}`;
  else path += ` V ${y + height} H ${x + width - radius}`;
  path += ` H ${x + radius}`;
  if (bl) path += ` A ${radius},${radius} 0 0 1 ${x},${y + height - radius}`;
  else path += ` H ${x} V ${y + height - radius}`;
  path += ` V ${y + radius}`;
  if (tl) path += ` A ${radius},${radius} 0 0 1 ${x + radius},${y}`;
  else path += ` V ${y} H ${x + radius}`;
  path += ` Z`;

  return path;
}
